import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { SignOut, ChartBar, FolderOpen, UsersThree, CreditCard, PaintBrush, ImageSquare, User, House, Receipt, UserPlus, EnvelopeSimple, Megaphone, ChatCircleText, Archive, NewspaperClipping, Tag, FileText, Calendar } from "@phosphor-icons/react";

const LOGO_URL =
  "/vznlogo.png";

export function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const items = [
    { to: "/admin", label: "Dashboard", icon: ChartBar },
    { to: "/admin/projects", label: "Projects", icon: FolderOpen },
    { to: "/admin/archive", label: "Archive", icon: Archive },
    { to: "/admin/clients", label: "Clients", icon: UsersThree },
    { to: "/admin/designers", label: "Designers", icon: PaintBrush },
    { to: "/admin/portfolio", label: "Portfolio", icon: ImageSquare },
    { to: "/admin/reviews", label: "Reviews", icon: ChatCircleText },
    { to: "/admin/payments", label: "Payments", icon: CreditCard },
    { to: "/admin/invoices", label: "Invoices", icon: FileText },
    { to: "/admin/receipts", label: "Receipts", icon: Receipt },
    { to: "/admin/meetings", label: "Meetings", icon: Calendar },
    { to: "/admin/charges", label: "Charges", icon: Receipt },
    { to: "/admin/promo-codes", label: "Promos", icon: Tag },
    { to: "/admin/email", label: "Email", icon: EnvelopeSimple },
    { to: "/admin/maintenance/broadcast", label: "Broadcast", icon: Megaphone },
    { to: "/admin/digest", label: "Digest", icon: NewspaperClipping },
    { to: "/account", label: "Account", icon: User },
    { to: "/", label: "Home", icon: House },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#fafafa] flex">
      <aside className="hidden lg:flex flex-col w-64 border-r border-[#262626] bg-[#0a0a0a] sticky top-0 h-screen">
        <Link to="/" className="px-6 py-5 border-b border-[#262626] flex items-center" data-testid="admin-brand" title="Back to home">
          <img src={LOGO_URL} alt="Vision Grafix · Back to home" className="w-[13rem] h-auto object-contain" />
        </Link>
        <nav className="flex-1 py-4">
          <Link
            to="/admin/clients?new=1"
            data-testid="admin-quick-add-client"
            className="mx-4 mb-3 flex items-center gap-2 px-3 py-2.5 text-xs font-bold uppercase tracking-wider border border-[#FF2D2D] text-[#FF2D2D] hover:bg-[#FF2D2D] hover:text-white transition-colors"
          >
            <UserPlus size={14} weight="bold" /> Add client
          </Link>
          {items.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.to || (item.to === "/admin/projects" && location.pathname.startsWith("/admin/projects"));
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-6 py-3 text-sm border-l-2 transition-colors ${
                  isActive
                    ? "border-[#FF2D2D] bg-[#121212] text-white"
                    : "border-transparent text-[#a3a3a3] hover:text-white hover:bg-[#0f0f0f]"
                }`}
              >
                <Icon size={18} weight={isActive ? "fill" : "regular"} />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-[#262626]">
          <div className="text-xs text-[#a3a3a3]" data-testid="admin-user-name">{user?.name}</div>
          <button
            onClick={handleLogout}
            className="mt-2 btn-outline px-3 py-1.5 text-xs flex items-center gap-2 w-full justify-center"
            data-testid="admin-logout"
          >
            <SignOut size={14} /> Sign out
          </button>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-40 backdrop-blur-xl bg-[#050505]/70 border-b border-[#262626]">
          <div className="px-6 py-3 flex items-center justify-between">
            <img src={LOGO_URL} alt="Vision Grafix" className="w-[10rem] h-auto object-contain" />
            <button onClick={handleLogout} className="text-sm text-[#a3a3a3]">Sign out</button>
          </div>
          <nav className="flex overflow-x-auto border-t border-[#262626]">
            {items.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-4 py-3 text-xs whitespace-nowrap ${
                  location.pathname === item.to ? "text-[#FF2D2D]" : "text-[#a3a3a3]"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </header>
        <main className="px-6 md:px-10 py-8">{children}</main>
      </div>
    </div>
  );
}
